import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps, useAsyncModal } from "@msuite/ui/modal";
import { Fragment, responseHandler } from "@msuite/ui/utils";
import {
	type SubstitutionPlanFrontendFormSchema,
	type SubstitutionPlanSubmitQuerySchema,
	substitutionPlanFrontendFormSchema,
} from "@msuite/utils/schemas";
import { guardString } from "@msuite/utils/strings";
import { validateSubstitutionPlan } from "@msuite/utils/substitutions";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { SubstitutionPlanForm } from "@/forms/substitution-plan-form";
import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";
import { SubstitutionPlanCopyPreviousModal } from "@/modals/users/substitution-plan-copy-previous-modal";
import { SubstitutionPlanSubmitModal } from "@/modals/users/substitution-plan-submit-modal";
import { useDataTableContext } from "@msuite/ui/table";
import { ModalLoadFailed } from "@/components/modals";

interface SubstitutionPlanModalProps extends ModalProps {
	absenceId: string;
	startDate: string;
	endDate: string;
}

export function SubstitutionPlanModal({
	isOpen,
	onClose,
	absenceId,
	startDate,
	endDate,
}: SubstitutionPlanModalProps) {
	const auth = useAuth();
	const { t } = useTranslation();
	const tableContext = useDataTableContext();
	const asyncModal = useAsyncModal<SubstitutionPlanSubmitQuerySchema>();
	const copyPreviousModal = useAsyncModal<boolean>();

	/** Query */
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const preview = await fetchSubstitutionPlanForm(false, false);
		if (preview.initialized) return { items: preview.substitutionPlanForm };

		const copyPrevious = preview.hasPreviousPlan ? await copyPreviousModal.confirm() : false;
		const initialized = await fetchSubstitutionPlanForm(true, !!copyPrevious);
		return { items: initialized.substitutionPlanForm };
	});
	const methods = useForm({
		resolver: resolver(substitutionPlanFrontendFormSchema),
		defaultValues,
	});
	const items = useWatch({ control: methods.control, name: "items" });
	const validation = validateSubstitutionPlan(items);

	/** Functions */
	async function fetchSubstitutionPlanForm(initialize: boolean, copyPrevious: boolean) {
		if (!auth.user?.id) throw new Error("UserId Missing");
		const response = await client.users[":userId"].substitutions[":absenceId"].form.$get({
			param: {
				userId: auth.user.id,
				absenceId,
			},
			query: {
				initialize: String(initialize),
				copyPrevious: String(copyPrevious),
			},
		});
		return responseHandler(response);
	}

	async function handleOnSubmit(json: SubstitutionPlanFrontendFormSchema) {
		let query: SubstitutionPlanSubmitQuerySchema | undefined;

		if (validation.isValid) {
			const queryResponse = await handleOnSubmitSummaryForm();
			if (typeof queryResponse !== "boolean") query = queryResponse;
		}

		const response = await client.users[":userId"].substitutions[":absenceId"].form.$patch({
			param: {
				userId: guardString(auth.user?.id),
				absenceId,
			},
			json,
			query: {
				submit: String(query?.submit ?? false),
				schedule: String(query?.schedule ?? false),
				sendEmailsScheduleDate: query?.sendEmailsScheduleDate || undefined,
			},
		});
		await responseHandler(response);
		await tableContext?.refetch?.();
		onClose();
	}

	async function handleOnSubmitSummaryForm() {
		const query = await asyncModal.confirm();
		return query;
	}

	/** Render */
	return (
		<Fragment>
			<FormProvider {...methods}>
				<Modal
					title={t("substitutions.substitutions")}
					isOpen={isOpen}
					onClose={onClose}
					width="95vw"
					onSubmit={methods.handleSubmit(handleOnSubmit)}
					isLoading={methods.formState.isLoading && !loadFailed}
				>
					{loadFailed ? (
						<ModalLoadFailed onClose={onClose} />
					) : (
						<>
							<Modal.Body>
								<SubstitutionPlanForm
									absenceId={absenceId}
									startDate={startDate}
									endDate={endDate}
									validation={validation}
								/>
							</Modal.Body>
							<Modal.FooterForm
								submitButtonProps={{
									variant: validation.isValid ? "accent" : "primary",
									icon: validation.isValid ? <Icon name="CheckCheck" /> : undefined,
								}}
								submitLabel={validation.isValid ? t("general.continue") : t("general.save")}
							/>
						</>
					)}
				</Modal>
			</FormProvider>
			<SubstitutionPlanSubmitModal
				controller={asyncModal}
				startDate={startDate}
			/>
			<SubstitutionPlanCopyPreviousModal controller={copyPreviousModal} />
		</Fragment>
	);
}
